import React, { useEffect, useMemo, useState } from 'react';
import NumInput from '../../../components/ui/NumInput';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { ArrowLeft, ArrowRight, Check, Star, Percent, Tablet, Loader2, PartyPopper, QrCode, Layers, ScanLine } from 'lucide-react';
import QrCheckinCard from '../../../components/qr/QrCheckinCard';
import CardScanCard from '../../../components/qr/CardScanCard';
// Marca nueva (8-oct-2026): misma tipografía y paleta que el registro y lealify.com.
import '@fontsource-variable/plus-jakarta-sans';
const FUENTE = { fontFamily: "'Plus Jakarta Sans Variable', system-ui, sans-serif" };
import { baseApi } from '../../../api/baseClient';
import { useAuth } from '../../../context/AuthContext';
import { useError } from '../../../context/ErrorContext';
import { CATEGORIES, CASHBACK_PRESETS, POINTS_PRESETS, getCategory, LoyaltyMode } from '../data/wizardData';

// Wizard de onboarding post-registro (Fase 1 plan de conversión).
// Reemplaza el redirect al formulario completo de Mi Negocio: pide SOLO lo
// urgente (giro → modo de premio → primera recompensa) y cierra con el
// aha-moment: "regístrate tú como tu primer cliente" en el kiosco.
// Plan: docs/lealify-wizard-onboarding-plan.md

const TOTAL_STEPS = 4; // pantallas con input; la final (5) es celebración
// Paso 4 (8-oct-2026, Carlos): «¿Cómo registrarán sus visitas?» — kiosco, QR impreso o los dos. Para quien no
// tiene o no quiere tablet en el mostrador (el freno que más se repite en los chats). El QR solo se prende si lo eligen.
// 'escaner' (23-oct-2026): tarjeta QR del cliente, el empleado la lee con su celular (sin tablet).
type Metodo = 'kiosco' | 'qr' | 'ambos' | 'escaner';
const FINAL = 4;

const OnboardingWizardPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, updateBusinessInfo, markOnboardingComplete } = useAuth();
  const { handleFirebaseError } = useError();

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Selecciones
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [mode, setMode] = useState<LoyaltyMode>('visits');
  const [cashbackPct, setCashbackPct] = useState(10);
  const [pointsPerVisit, setPointsPerVisit] = useState(1);
  const [templateIdx, setTemplateIdx] = useState(0);
  const [rewardTitle, setRewardTitle] = useState('');
  const [rewardCost, setRewardCost] = useState(0);

  const category = useMemo(() => getCategory(categoryId), [categoryId]);
  const businessName = currentUser?.businessName || 'tu negocio';

  // Auto-login del kiosco (fuga 1 onboarding): al llegar a la pantalla final se pide un
  // token de un solo uso (TTL 30 min) y se pega a la URL/QR de app.lealify.com — el dueño
  // aterriza en el kiosco YA logueado, sin re-teclear las credenciales que acaba de crear.
  // Si la petición falla, se queda la URL pelona y el login manual de siempre (fail-safe).
  const [kioskUrl, setKioskUrl] = useState('https://app.lealify.com');
  const [metodo, setMetodo] = useState<Metodo>('kiosco');
  useEffect(() => {
    if (step !== FINAL) return;
    let cancelled = false;
    baseApi.post<{ token: string }>('/auth/kiosk-token', {})
      .then((r) => { if (!cancelled && r?.token) setKioskUrl(`https://app.lealify.com/?kt=${r.token}`); })
      .catch(() => { /* sin token → login manual como antes */ });
    return () => { cancelled = true; };
  }, [step]);

  // Los admin no pasan por aquí (no tienen negocio)
  useEffect(() => {
    if (currentUser && (currentUser.role === 'admin' || !currentUser.businessId)) {
      markOnboardingComplete().finally(() => navigate('/dashboard', { replace: true }));
    }
  }, [currentUser]);

  // Si ya completó onboarding (entró directo a /onboarding), fuera
  useEffect(() => {
    if (currentUser?.hasCompletedOnboarding) navigate('/dashboard', { replace: true });
  }, []);

  const track = (event: string) => { (window as any).gtag?.('event', event); };

  // Al elegir giro: precargar sugerencias
  const pickCategory = (id: string) => {
    setCategoryId(id);
    const cat = getCategory(id);
    setMode(cat.suggestedMode);
    setCashbackPct(cat.suggestedCashbackPct);
    setPointsPerVisit(cat.suggestedPointsPerVisit);
    setTemplateIdx(0);
    track('onboarding_step_1');
    setStep(1);
  };

  // Al confirmar modo: precargar plantilla
  const confirmMode = () => {
    const t = category.templates[0];
    setTemplateIdx(0);
    setRewardTitle(t.title);
    setRewardCost(mode === 'cashback' ? t.cashbackCost : t.visitsCost * pointsPerVisit);
    track('onboarding_step_2');
    setStep(2);
  };

  const pickTemplate = (idx: number) => {
    setTemplateIdx(idx);
    const t = category.templates[idx];
    setRewardTitle(t.title);
    setRewardCost(mode === 'cashback' ? t.cashbackCost : t.visitsCost * pointsPerVisit);
  };

  // TODO IncX: la persistencia ligera de progreso (reanudar wizard a medias) escribía en
  // users/{uid} de Firestore; base aún no tiene endpoint para ese borrador. Se omite: el
  // checklist del dashboard ya guía a quien abandona a medias.

  // Paso 3 → crear recompensa + guardar config + marcar completo
  const finishSetup = async () => {
    if (!currentUser?.businessId || saving) return;
    if (!rewardTitle.trim() || rewardCost <= 0) return;
    setSaving(true);
    try {
      const t = category.templates[templateIdx];
      const cost = mode === 'cashback' ? parseFloat(Number(rewardCost).toFixed(2)) : Math.floor(rewardCost);

      // Primera recompensa → catálogo de lealtad en base.
      await baseApi.post('/loyalty/rewards', {
        title: rewardTitle.trim(),
        description: t.description,
        points: cost,
        image_url: t.imageUrl,
      });

      // Config del programa (modo/puntos/%) → /loyalty/settings vía el contexto.
      await updateBusinessInfo(currentUser.businessId, {
        loyaltyMode: mode,
        cashbackPercentage: cashbackPct,
        pointsPerVisit,
      } as any);

      await markOnboardingComplete();
      track('onboarding_step_3');

      // TODO IncX: correo de bienvenida — el endpoint esapi exigía idToken de Firebase Auth,
      // ya retirado. En base se cubrirá con su propio mailer (o el cron de rescate).

      setStep(3); // → ¿cómo registrarán sus visitas? (el negocio ya quedó configurado y completo)
    } catch (e) {
      handleFirebaseError(e, 'onboarding-wizard');
    } finally {
      setSaving(false);
    }
  };

  // Paso 4: método de registro. Kiosco = como siempre (nada que guardar). QR / ambos = se prende el QR.
  const confirmMetodo = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (metodo === 'escaner') {
        await baseApi.put('/loyalty/settings', { card_scan_enabled: 1 });
        window.dispatchEvent(new Event('lealify:card-scan-changed'));
      } else if (metodo !== 'kiosco') {
        await baseApi.put('/loyalty/settings', { qr_checkin_enabled: 1 });
        window.dispatchEvent(new Event('lealify:qr-checkin-changed'));
      }
      (window as any).gtag?.('event', 'onboarding_metodo', { metodo });
      track('wizard_complete');
      setStep(FINAL);
    } catch (e) {
      handleFirebaseError(e, 'onboarding-wizard');
    } finally {
      setSaving(false);
    }
  };

  // Saltar: no encerramos a nadie; el checklist del dashboard lo guía después.
  // 1-nov-2026 (Carlos): quien saltaba ANTES del paso 3 quedaba sin programa ni recompensa (9 negocios desde sep:
  // Minisuper Lala, Nativa Café…) y su cliente no tenía nada que ganar. Ahora «Saltar» deja la configuración base
  // de su giro (o la genérica si no eligió) con la primera recompensa de la plantilla, igual que el paso 3.
  // Si ya pasó el paso 3 (step >= 3) ya está configurado y no se toca. Si falla, se salta igual (no encerrar).
  const configBase = async (): Promise<boolean> => {
    if (step >= 3 || !currentUser?.businessId) return false;
    try {
      const cat = getCategory(categoryId);
      const m: LoyaltyMode = categoryId ? mode : cat.suggestedMode;
      const ppv = categoryId ? pointsPerVisit : cat.suggestedPointsPerVisit;
      const pct = categoryId ? cashbackPct : cat.suggestedCashbackPct;
      const t = cat.templates[0];
      await baseApi.post('/loyalty/rewards', {
        title: t.title,
        description: t.description,
        points: m === 'cashback' ? parseFloat(Number(t.cashbackCost).toFixed(2)) : Math.floor(t.visitsCost * ppv),
        image_url: t.imageUrl,
      });
      await updateBusinessInfo(currentUser.businessId, { loyaltyMode: m, cashbackPercentage: pct, pointsPerVisit: ppv } as any);
      return true;
    } catch (e) {
      console.error('[onboarding] configuración base al saltar:', e);
      return false;
    }
  };

  const skip = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const paso = step;
      const base = await configBase();
      track('wizard_skip');
      (window as any).gtag?.('event', 'wizard_skip_config_base', { paso, base: base ? 'si' : 'no' });
      await markOnboardingComplete();
      navigate('/dashboard', { replace: true });
    } finally {
      setSaving(false);
    }
  };

  const unitLabel = mode === 'cashback' ? 'pesos' : (pointsPerVisit === 1 ? 'visitas' : 'puntos');

  return (
    <div className="relative min-h-screen flex-shrink-0 overflow-hidden bg-[#0E1E3A] flex flex-col items-center px-4 py-8" style={FUENTE}>
      {/* Halos azules de la marca (decorativos) */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 -left-40 h-[28rem] w-[28rem] rounded-full bg-[#0057B8]/40 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-48 -right-40 h-[32rem] w-[32rem] rounded-full bg-[#0057B8]/30 blur-3xl" />
      {/* Logo + progreso */}
      <div className="relative w-full max-w-2xl">
        <div className="flex items-center justify-between mb-4">
          <svg viewBox="0 0 300 120" className="h-7 w-auto" style={{ fill: '#ffffff' }}>
            <path d="M262.5,120,272.21,98.96,249.435,44.573h14.586l15.4,38.445L294.6,44.573h14.044L276.764,120ZM146.57,94.406l-.326-1.3c-3.632,6.29-8.838,10.3-16.809,10.3-10.845,0-17.949-6.073-17.949-15.617,0-9.6,6.182-14.478,18.707-17.027l11.062-2.223c3.416-.651,4.989-2.006,4.989-5.314,0-5.206-3.687-8.405-9.868-8.405-6.4,0-9.869,2.874-10.737,7.537L113,61.492c1.193-9.977,9.869-18.166,23.913-18.166,9.382,0,16.484,3.361,19.684,9.327,1.572,2.983,2.44,6.561,2.44,12.851V88.766c0,2.332.977,3.416,2.712,3.525a12.729,12.729,0,0,0,2.006-.109V102.92a27.663,27.663,0,0,1-4.772.542C152.644,103.461,148.035,100.371,146.57,94.406Zm-6.127-17.352-7.538,2.007c-6.4,1.68-8.73,4.446-8.73,7.862,0,3.741,2.982,5.639,7.753,5.639,6.833,0,14.37-4.664,14.37-15.075V74.831C145.106,75.59,143.968,76.078,140.443,77.054ZM50.592,73.366c0-18,13.177-30.094,28.143-30.094,16.972,0,27.492,12.959,27.6,29.173a38.53,38.53,0,0,1-.325,4.989h-42.4c.868,8.3,7.213,14.261,16.16,14.261,6.453,0,10.519-3.2,12.526-7.537L104.6,85.946c-3.2,10.52-12.254,17.407-24.781,17.407C62.467,103.353,50.592,91.641,50.592,73.366Zm13.123-5.531H93.1c-1.355-7.646-6.127-12.851-14.532-12.851C71.252,54.985,65.179,60.081,63.715,67.836Zm144.13,34.216H170.592V25.54h12.8V89.417h11.875V44.573h12.743v57.479ZM0,102.052V0H43.543V76.512H30.2v-64.2H13.394V89.743H43.543v12.309ZM225.034,102V56.014H214.46v-11.5h10.628v-2.44c0-10.085,5.693-17.84,17.3-17.84a27.308,27.308,0,0,1,7.429.868l-1.193,11.5a16.918,16.918,0,0,0-4.555-.434c-4.066,0-6.29,2.006-6.29,6.4v2.007H248.4V56.014H237.776V102Zm-30.8-70.059a7.429,7.429,0,1,1,7.429,7.43A7.429,7.429,0,0,1,194.234,31.938Z" />
          </svg>
          {step < FINAL && (
            <button onClick={step === 3 ? () => { setMetodo('kiosco'); setStep(FINAL); } : skip} disabled={saving} className="text-white/70 text-sm hover:text-white transition-colors">
              Saltar por ahora
            </button>
          )}
        </div>
        {step < FINAL && (
          <div className="mb-6">
            <div className="w-full bg-white/15 rounded-full h-1.5">
              <div className="bg-white h-1.5 rounded-full transition-all duration-500" style={{ width: `${((step + 1) / TOTAL_STEPS) * 100}%` }} />
            </div>
            <p className="mt-2 text-xs font-semibold text-white/60">Paso {step + 1} de {TOTAL_STEPS}</p>
          </div>
        )}
      </div>

      <div className="relative w-full max-w-2xl bg-surface rounded-[28px] shadow-2xl shadow-black/30 p-6 sm:p-9 animate-fade-in-up">

        {/* ---------- PASO 0: Giro ---------- */}
        {step === 0 && (
          <div>
            <h1 style={FUENTE} className="text-2xl sm:text-[28px] font-extrabold tracking-tight text-content mb-1">¿Qué tipo de negocio es {businessName}?</h1>
            <p className="text-content-2 text-sm mb-6">Con esto te sugerimos la mejor forma de premiar a tus clientes.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {CATEGORIES.map(c => (
                <button key={c.id} onClick={() => pickCategory(c.id)}
                  className="group border border-line rounded-2xl p-4 text-center hover:border-[#0057B8] hover:shadow-lg transition-all hover:scale-[1.03] bg-surface">
                  <div className="text-4xl mb-2">{c.emoji}</div>
                  <div className="text-sm font-semibold text-content group-hover:text-[#0057B8]">{c.label}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ---------- PASO 1: Modo ---------- */}
        {step === 1 && (
          <div>
            <h1 style={FUENTE} className="text-2xl sm:text-[28px] font-extrabold tracking-tight text-content mb-1">¿Cómo quieres premiar?</h1>
            <p className="text-content-2 text-sm mb-6">Elegimos la opción más usada por negocios como el tuyo — puedes cambiarla cuando quieras.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {/* Puntos */}
              <button onClick={() => setMode('visits')}
                className={`relative text-left border-2 rounded-xl p-4 transition-all ${mode === 'visits' ? 'border-[#0057B8] bg-[#0057B8]/5 dark:bg-blue-500/10 shadow-md' : 'border-line hover:border-[#0057B8]/40'}`}>
                {category.suggestedMode === 'visits' && (
                  <span className="absolute -top-2.5 left-3 bg-[#0057B8] text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">Recomendado para tu giro</span>
                )}
                <div className="flex items-center mb-2"><Star className="h-5 w-5 text-yellow-500 mr-2" fill="currentColor" /><span className="font-bold text-content">Puntos por visita</span></div>
                <p className="text-xs text-content-2">Cada visita suma puntos fijos, sin importar cuánto gaste. Ideal para tickets parecidos y visitas frecuentes.</p>
              </button>
              {/* Cashback */}
              <button onClick={() => setMode('cashback')}
                className={`relative text-left border-2 rounded-xl p-4 transition-all ${mode === 'cashback' ? 'border-emerald-600 bg-emerald-50 dark:bg-emerald-500/10 shadow-md' : 'border-line hover:border-emerald-300'}`}>
                {category.suggestedMode === 'cashback' && (
                  <span className="absolute -top-2.5 left-3 bg-emerald-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">Recomendado para tu giro</span>
                )}
                <div className="flex items-center mb-2"><Percent className="h-5 w-5 text-emerald-600 mr-2" /><span className="font-bold text-content">Cashback (dinero)</span></div>
                <p className="text-xs text-content-2">Gana un % de cada compra como saldo para gastar contigo. Ideal para tickets variables o altos.</p>
              </button>
            </div>

            {/* Valor según modo */}
            {mode === 'visits' ? (
              <div className="mb-6">
                <label className="block text-sm font-medium text-content-2 mb-2">Puntos por visita</label>
                <div className="flex gap-2">
                  {POINTS_PRESETS.map(p => (
                    <button key={p} onClick={() => setPointsPerVisit(p)}
                      className={`px-5 py-2 rounded-lg border font-semibold text-sm transition-colors ${pointsPerVisit === p ? 'bg-[#0057B8] text-white border-[#0057B8]' : 'border-line text-content-2 hover:border-blue-400'}`}>
                      {p}
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-content-3">Lo más simple: 1 punto = 1 visita.</p>
              </div>
            ) : (
              <div className="mb-6">
                <label className="block text-sm font-medium text-content-2 mb-2">Porcentaje de cashback</label>
                <div className="flex gap-2">
                  {CASHBACK_PRESETS.map(p => (
                    <button key={p} onClick={() => setCashbackPct(p)}
                      className={`px-5 py-2 rounded-lg border font-semibold text-sm transition-colors ${cashbackPct === p ? 'bg-emerald-600 text-white border-emerald-600' : 'border-line text-content-2 hover:border-emerald-400'}`}>
                      {p}%
                    </button>
                  ))}
                  {/* Otro porcentaje (29-oct-2026, Dave / Bataker quería 1 %): antes solo había presets. */}
                  <div className="relative w-24">
                    <NumInput min={0.5} max={100} step={0.5} inputMode="decimal" placeholder="Otro"
                      value={CASHBACK_PRESETS.includes(cashbackPct) ? null : cashbackPct}
                      onValue={(n) => setCashbackPct(n)}
                      className={`block w-full rounded-lg border px-3 py-2 pr-7 text-sm font-semibold bg-transparent text-content focus:ring-accent focus:border-blue-500 ${CASHBACK_PRESETS.includes(cashbackPct) ? 'border-line' : 'border-emerald-600'}`} />
                    <span className="absolute right-2 top-2 text-sm text-content-3">%</span>
                  </div>
                </div>
                <p className="mt-2 text-xs text-content-3">Ejemplo: una venta de $200 le abona <strong>${(200 * cashbackPct / 100).toFixed(0)}</strong> a tu cliente.</p>
              </div>
            )}

            <div className="flex justify-between">
              <button onClick={() => setStep(0)} className="flex items-center text-sm text-content-2 hover:text-content"><ArrowLeft className="h-4 w-4 mr-1" /> Atrás</button>
              <button onClick={confirmMode} className="flex items-center px-5 py-2.5 bg-[#0057B8] hover:bg-[#004494] text-white font-bold text-sm rounded-full px-6 transition-colors">
                Continuar <ArrowRight className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        )}

        {/* ---------- PASO 2: Primera recompensa ---------- */}
        {step === 2 && (
          <div>
            <h1 style={FUENTE} className="text-2xl sm:text-[28px] font-extrabold tracking-tight text-content mb-1">Tu primera recompensa 🎁</h1>
            <p className="text-content-2 text-sm mb-6">El premio por el que tus clientes van a regresar. Elige una y ajústala a tu gusto.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {category.templates.map((t, i) => (
                <button key={i} onClick={() => pickTemplate(i)}
                  className={`text-left border-2 rounded-xl overflow-hidden transition-all ${templateIdx === i ? 'border-[#0057B8] shadow-md' : 'border-line hover:border-[#0057B8]/40'}`}>
                  <img src={t.imageUrl} alt={t.title} className="w-full h-28 object-cover" />
                  <div className="p-3">
                    <div className="font-bold text-content text-sm">{t.title}</div>
                    <div className="text-xs text-content-2 mt-0.5">
                      Cuesta {mode === 'cashback' ? `$${t.cashbackCost}` : `${t.visitsCost * pointsPerVisit} ${t.visitsCost * pointsPerVisit === 1 ? 'punto' : 'puntos'}`}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-content-2 mb-1">Nombre de la recompensa</label>
                <input type="text" value={rewardTitle} onChange={e => setRewardTitle(e.target.value)}
                  className="block w-full rounded-md border border-line bg-transparent text-content px-3 py-2 text-sm focus:ring-accent focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-content-2 mb-1">Costo ({unitLabel})</label>
                <div className="relative">
                  {mode === 'cashback' && <span className="absolute left-3 top-2 text-content-3 text-sm">$</span>}
                  <NumInput min={1} value={rewardCost}
                    onValue={setRewardCost}
                    className={`block w-full rounded-md border border-line bg-transparent text-content py-2 text-sm focus:ring-accent focus:border-blue-500 ${mode === 'cashback' ? 'pl-7 pr-3' : 'px-3'}`} />
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <button onClick={() => setStep(1)} className="flex items-center text-sm text-content-2 hover:text-content"><ArrowLeft className="h-4 w-4 mr-1" /> Atrás</button>
              <button onClick={finishSetup} disabled={saving || !rewardTitle.trim() || rewardCost <= 0}
                className="flex items-center px-5 py-2.5 bg-[#0057B8] hover:bg-[#004494] text-white font-bold text-sm rounded-full px-6 transition-colors disabled:opacity-50">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
                {saving ? 'Guardando…' : 'Crear y terminar'}
              </button>
            </div>
          </div>
        )}

        {/* ---------- PASO 3: ¿Cómo registrarán sus visitas? ---------- */}
        {step === 3 && (
          <div>
            <h1 style={FUENTE} className="text-2xl sm:text-[28px] font-extrabold tracking-tight text-content mb-1">¿Cómo registrarán sus visitas tus clientes?</h1>
            <p className="text-content-2 text-sm mb-6">Puedes cambiarlo cuando quieras en Mi Negocio → Cómo registro visitas.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {([
                { id: 'kiosco', icon: Tablet, titulo: 'Tablet o celular en el mostrador', texto: 'Tu cliente teclea su número al pagar. Sirve un celular viejo o la computadora de la caja.' },
                { id: 'qr', icon: QrCode, titulo: 'QR impreso, sin equipo', texto: 'Pegas un cartel y tu cliente lo escanea con su celular. Tú confirmas su visita en tu panel.' },
                { id: 'escaner', icon: ScanLine, titulo: 'Tarjeta del cliente, tú la escaneas', texto: 'Cada cliente guarda su tarjeta QR en el celular. Al pagar, la escaneas con el tuyo y la visita entra al instante.' },
                { id: 'ambos', icon: Layers, titulo: 'Kiosco + QR impreso', texto: 'Kiosco en el mostrador y el QR para cuando haya fila o no haya quién atienda.' },
              ] as { id: Metodo; icon: React.ElementType; titulo: string; texto: string }[]).map((o) => {
                const Icon = o.icon; const sel = metodo === o.id;
                return (
                  <button key={o.id} onClick={() => setMetodo(o.id)}
                    className={`relative text-left border-2 rounded-xl p-4 transition-all ${sel ? 'border-[#0057B8] bg-[#0057B8]/5 dark:bg-blue-500/10 shadow-md' : 'border-line hover:border-[#0057B8]/40'}`}>
                    {(o.id === 'qr' || o.id === 'escaner') && <span className="absolute -top-2.5 left-3 bg-green-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">Sin tablet · Gratis</span>}
                    <Icon className={`h-6 w-6 mb-2 ${sel ? 'text-[#0057B8] dark:text-blue-300' : 'text-content-3'}`} />
                    <div className="font-bold text-content text-sm">{o.titulo}</div>
                    <p className="text-xs text-content-2 mt-1">{o.texto}</p>
                  </button>
                );
              })}
            </div>
            <div className="flex justify-end">
              <button onClick={confirmMetodo} disabled={saving}
                className="flex items-center px-5 py-2.5 bg-[#0057B8] hover:bg-[#004494] text-white font-bold text-sm rounded-full px-6 transition-colors disabled:opacity-50">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Continuar <ArrowRight className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        )}

        {/* ---------- FINAL: el aha ---------- */}
        {step === FINAL && (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-500/10 mb-4">
              <PartyPopper className="h-8 w-8 text-green-600" />
            </div>
            <h1 style={FUENTE} className="text-2xl sm:text-[28px] font-extrabold tracking-tight text-content mb-2">¡{businessName} está listo! 🎉</h1>
            {metodo === 'escaner' && (
              <div className="max-w-md mx-auto mb-6 text-left">
                <p className="text-content-2 text-sm mb-3 text-center">
                  Tu escáner ya está activo. Pruébalo: abre el kiosco con tu propio celular, guarda
                  <strong> tu tarjeta</strong> y escanéala desde <strong>Escanear tarjeta</strong> en tu panel.
                </p>
                <CardScanCard variante="compacta" />
              </div>
            )}
            {(metodo === 'qr' || metodo === 'ambos') && (
              <div className="max-w-md mx-auto mb-6 text-left">
                <p className="text-content-2 text-sm mb-3 text-center">
                  Tu QR ya está activo: <strong>imprime el cartel</strong> y pégalo en tu mostrador. Pruébalo tú
                  primero escaneándolo con tu celular y confirma la visita en tu panel (Visitas por QR).
                </p>
                <QrCheckinCard variante="compacta" />
              </div>
            )}

            {metodo !== 'qr' && (<>
            <p className="text-content-2 text-sm mb-6 max-w-md mx-auto">
              Ahora <strong>vívelo como tu cliente</strong>: abre el kiosco, teclea tu propio celular y mira
              cómo suman tus {mode === 'cashback' ? 'pesos' : 'puntos'} y cuánto te falta para <strong>{rewardTitle}</strong>.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center max-w-md mx-auto mb-6">
              <a href={kioskUrl} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center px-5 py-3 bg-[#0057B8] hover:bg-[#004494] text-white font-bold text-sm rounded-full transition-colors">
                <Tablet className="h-5 w-5 mr-2" /> Abrir el kiosco
              </a>
              <div className="flex flex-col items-center">
                <div className="bg-white p-2 rounded-lg border border-line">
                  <QRCodeSVG value={kioskUrl} size={96} />
                </div>
                <span className="text-[11px] text-content-3 mt-1">o escanéalo con tu celular</span>
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 rounded-lg p-3 text-xs text-blue-800 dark:text-blue-300 max-w-md mx-auto mb-6 text-left">
              <strong>Tip:</strong> este botón y QR te dejan dentro del kiosco sin volver a escribir tu
              contraseña (funciona los próximos 30 min; después entra con este mismo correo y contraseña).
              Déjalo abierto en la tablet, celular o computadora de tu mostrador: tus clientes teclean su
              número al pagar y listo.
            </div>
            </>)}

            <button onClick={() => navigate('/dashboard', { replace: true })}
              className="text-sm font-bold text-[#0057B8] dark:text-blue-300 hover:underline">
              Ir a mi panel →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingWizardPage;
